/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import { allowUpdateEntity, allowDeleteEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { UserInfo } from "../../../shared/types/user-info";
import { CompanyService } from "../companies/company.service";
import { getLocationSearchFilterOptions, STATES } from "./location";
import { Location } from "./location.type";
import { LocationService } from "./location.service";

@Injectable({
  providedIn: "root",
})
export class LocationTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private locationService: LocationService,
    private dataTableService: DataTableService<Location>,
    private companyService: CompanyService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getLocationsTableConfig = (config?: DataTableConfig): DataTable<Location> => {
    const { hidden, shown, actionColumn, entities } = config || {};

    const columns: DataTableColumn<Location>[] = (
      [
        {
          prop: "name",
          name: "Name",
          link: {
            path: (location) => `/locations/${location.id}`,
          },
          inlineEdit: {
            control: "input",
          },
        },
        {
          prop: "address1",
          name: "Address",
          inlineEdit: {
            control: "input",
          },
        },
        {
          prop: "city",
          name: "City",
          inlineEdit: {
            control: "input",
          },
        },
        {
          prop: "state",
          name: "State",
          inlineEdit: {
            control: "select",
            select: {
              options: [...STATES],
            },
          },
        },
        {
          prop: "zip",
          name: "Zip",
          inlineEdit: {
            control: "input",
          },
        },
        {
          prop: "country.name",
          name: "Country",
          inlineEdit: {
            control: "search",
            search: {
              columnToUpdate: "countryId",
              propToUpdate: "country",
              propToDisplay: "name",
              fetchDefaultData: true,
              service: (formValue: string) =>
                this.locationService.getCountries({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                  },
                }),
            },
          },
        },
        {
          prop: "phone",
          name: "Phone",
          inlineEdit: {
            control: "input",
          },
        },
        {
          prop: "company.name",
          name: "Company",
          hidden: this.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          link: {
            path: (contract) => `/companies/${contract.companyId}`,
            disabled: (contract) => this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
      ] as DataTableColumn<Location>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (location) =>
            col?.inlineEdit?.disabled?.(location) || !allowUpdateEntity(location, this.currentUser),
        },
      }));

    return {
      id: "locations",
      entityName: "Location",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "locations",
          data: {
            browser: (queryParams) =>
              this.locationService.getLocations({
                ...queryParams,
                where: { companyId: entities?.company?.id, ...queryParams.where },
              }),
            email: (queryParams) =>
              this.locationService.getLocationsQueue({
                ...queryParams,
                where: { companyId: entities?.company?.id, ...queryParams.where },
              }),
          },
        },
      },
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (location) => this.viewLocation(location, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageLocation,
              hidden: (location) => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteLocation,
              hidden: (location) =>
                actionColumn?.delete?.hidden ||
                (!this.currentUser.isGlobal() && location.isChannelWorks) ||
                !allowDeleteEntity(location, this.currentUser),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getLocationSearchFilterOptions(this.currentUser, {
            hidden,
            shown,
            companyService: this.companyService,
          }),
        },
      ],
    };
  };

  /** Populate table */
  getLocations = (event: GetDataEvent<Location>): void => {
    const { onError, onResult, queryParams } = event;
    this.locationService
      .getLocations({
        relations: { country: true, company: { partner: true } },
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Update table */
  update = (event: UpdateDataEvent<Location>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.locationService.updateLocation(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  // #region Private Methods

  private viewLocation = (location: Location, config?: DataTableConfig): void => {
    const columns = this.getLocationsTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({
        name: col.name,
        value: this.dataTableService.parseData(location, col, get(location, col.prop)),
      }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageLocation = (location: Location): void => {
    this.router.navigate([`/locations/${location.id}`], {
      queryParams: {
        assets_0_filter: "coverageStatus",
        assets_0_value: "active",
        assets_0_page: 1,
      },
    });
  };

  private deleteLocation = (location: Location, table: DataTableComponent<Location>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "location",
        data: location,
        url: this.locationService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };
  // #endregion
}
